import { createTheme } from '@mui/material/styles'

/*
 * Brand tokens. Kept as plain hex so they can be referenced from both
 * the theme and from component level sx props.
 */
const BRAND = {
  green: '#0C9347',
  greenDark: '#0A7A3C',
  greenDarker: '#085F2F',
  greenTint: 'rgba(12, 147, 71, 0.08)',
  greenRing: 'rgba(12, 147, 71, 0.35)',
  orange: '#F69220',
  orangeDark: '#DC7D14',
  orangeTint: 'rgba(246, 146, 32, 0.12)',
  cream: '#FBF7EC',
  ink: '#1A1A1A',
  inkMuted: '#5A5A5A',
  focus: '#0C9347'
}

export const theme = createTheme({
  palette: {
    primary: {
      main: BRAND.green,
      dark: BRAND.greenDark,
      darker: BRAND.greenDarker,
      light: '#2FA964',
      contrastText: '#fff'
    },
    secondary: {
      main: BRAND.orange,
      dark: BRAND.orangeDark,
      light: '#FFA94D',
      subMain: '#EA088B',
      contrastText: '#fff'
    },
    background: {
      default: '#FFFFFF',
      alt: BRAND.cream
    },
    text: {
      primary: BRAND.ink,
      secondary: BRAND.inkMuted
    },
    divider: 'rgba(26, 26, 26, 0.12)'
  },
  shape: {
    borderRadius: 8
  },
  typography: {
    fontFamily: 'Fira Sans, Roboto, Helvetica, Arial, sans-serif',
    h1: {
      fontFamily: 'Fira Sans',
      fontWeight: 700,
      lineHeight: 1.15,
      letterSpacing: '-0.015em'
    },
    h2: {
      fontFamily: 'Fira Sans',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.01em'
    },
    h3: {
      fontFamily: 'Fira Sans',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.005em'
    },
    h4: {
      fontFamily: 'Fira Sans',
      fontWeight: 600,
      lineHeight: 1.3
    },
    h5: {
      fontFamily: 'Fira Sans',
      fontWeight: 600,
      lineHeight: 1.35
    },
    h6: {
      fontFamily: 'Fira Sans',
      fontWeight: 600,
      lineHeight: 1.4
    },
    subtitle1: {
      fontFamily: 'Fira Sans',
      lineHeight: 1.6
    },
    subtitle2: {
      fontFamily: 'Fira Sans',
      lineHeight: 1.5
    },
    body1: {
      fontFamily: 'Fira Sans',
      lineHeight: 1.7
    },
    body2: {
      fontFamily: 'Fira Sans',
      lineHeight: 1.6
    },
    button: {
      fontFamily: 'Fira Sans',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: 0
    }
  },
  components: {
    /*
     * Ripples are disabled project wide, which removes the only feedback
     * the default MUI interaction states gave us. Focus rings and
     * transitions below put that affordance back in a calmer form.
     */
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          minHeight: 44,
          borderRadius: 50,
          paddingLeft: 24,
          paddingRight: 24,
          transitionProperty: 'background-color, color, border-color, box-shadow, transform',
          transitionDuration: '180ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)'
          },
          '&:active': {
            transform: 'translateY(0)'
          },
          '&:focus-visible': {
            outline: `3px solid ${BRAND.focus}`,
            outlineOffset: '2px'
          },
          ...(ownerState.variant === 'contained' &&
            ownerState.color === 'primary' && {
              backgroundColor: BRAND.orange,
              color: '#fff',
              textTransform: 'none',
              fontFamily: 'Fira Sans',
              fontWeight: 600,
              boxShadow: '0 2px 6px rgba(246, 146, 32, 0.24)',
              '&:hover': {
                backgroundColor: BRAND.orangeDark,
                boxShadow: '0 6px 16px rgba(246, 146, 32, 0.32)'
              }
            }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'primary' && {
              borderColor: BRAND.green,
              color: BRAND.green,
              textTransform: 'none',
              borderRadius: '50px',
              fontFamily: 'Fira Sans',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: BRAND.greenTint,
                borderColor: BRAND.greenDark
              }
            })
        })
      }
    },
    /*
     * Text fields appear in the contact form and the footer subscribe box.
     */
    MuiTextField: {
      defaultProps: {
        variant: 'outlined'
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          transitionProperty: 'box-shadow, background-color',
          transitionDuration: '180ms',
          '& fieldset': {
            transitionProperty: 'border-color',
            transitionDuration: '180ms'
          }
        },
        notchedOutline: {
          borderColor: 'rgba(26, 26, 26, 0.24)'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: 'Fira Sans',
          transitionProperty: 'color',
          transitionDuration: '180ms',
          '&.Mui-focused': {
            color: BRAND.green
          }
        }
      }
    },
    /*
     * Hover and focus treatment for card style surfaces.
     */
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          transitionProperty: 'transform, box-shadow',
          transitionDuration: '220ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 10px 28px rgba(26, 26, 26, 0.12)'
          }
        }
      }
    },
    MuiCardMedia: {
      styleOverrides: {
        root: {
          transitionProperty: 'transform',
          transitionDuration: '400ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transitionProperty: 'background-color, color',
          transitionDuration: '180ms',
          '&:focus-visible': {
            outline: `3px solid ${BRAND.focus}`,
            outlineOffset: '2px'
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Fira Sans',
          minHeight: 44,
          transitionProperty: 'background-color',
          transitionDuration: '120ms',
          '&:focus-visible': {
            outline: `3px solid ${BRAND.focus}`,
            outlineOffset: '-3px'
          }
        }
      }
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover'
      }
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            paddingLeft: 20,
            paddingRight: 20
          }
        }
      }
    }
  }
})
